import {
    collection,
    addDoc,
    getDoc,
    getDocs,
    query,
    orderBy,
    Timestamp,
    updateDoc,
    doc,
    runTransaction,
    collectionGroup,
    where
} from 'firebase/firestore';
import { db } from '../firebase/config';

export interface FinanceAccount {
    id?: string;
    type: 'bank' | 'cash' | 'petty_cash';
    name: string; // Display name (e.g. "Meezan Bank", "Cash in Hand (College)")
    openingBalance: number;
    balance: number;
    createdAt?: Date;
    updatedAt?: Date;

    // Bank-only optional fields
    bankName?: string;
    accountNumber?: string;
    accountTitle?: string;
    totalReceived?: number; // Total credits/revenue received by this account
}

export interface PettyCashWallet {
    id?: string;
    balance: number;
    totalReceived?: number;
    updatedAt?: Date;
}

export interface PettyCashTransferLine {
    fromAccountId: string;
    amount: number;
}

export interface PettyCashTransfer {
    id?: string;
    // legacy single-source fields
    fromAccountId?: string;
    amount?: number;

    // new multi-source fields
    lines?: PettyCashTransferLine[];
    totalAmount?: number;

    note?: string;
    performedBy: string;
    createdAt: Date;
}

export interface AccountTransfer {
    id?: string;
    fromAccountId: string;
    toAccountId: string;
    amount: number;
    note?: string;
    performedBy: string;
    createdAt: Date;
}

export interface PettyCashExpense {
    id?: string;
    description: string;
    amount: number;
    category: 'food' | 'equipment' | 'utilities' | 'transport' | 'other';
    date: Date;
    submittedBy: string;
    status: 'pending' | 'submitted' | 'approved' | 'rejected';
    notes?: string;
    batchId?: string;
    voucherNumber?: string; // e.g. JV-1512
    paymentType?: 'full' | 'part' | 'balance';
    totalAmount?: number; // Total amount for part payments
    paidAmount?: number;  // Current payment amount
    balanceAmount?: number; // Remaining balance
    createdAt?: Date;
}

export interface AccountPayable {
    id?: string;
    voucherNumber: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
    status: 'unpaid' | 'partial' | 'paid';
    description: string;
    category: string;
    date: Date;
    history: {
        date: Date;
        amount: number;
        performedBy: string;
        reference?: string;
    }[];
    updatedAt: Date;
}

export interface Revenue {
    id?: string;
    type: 'fee' | 'donation' | 'other';
    amount: number;
    date: Date;
    receivedBy: string;
    accountId?: string; // Finance account to credit

    // Common optional fields
    description?: string; // For 'other' or generic notes

    // Donation specific
    donorName?: string;
    paymentMethod?: 'cash' | 'bank_transfer' | 'check' | 'online';
    bankName?: string;
    accountNumber?: string;
    accountTitle?: string;
    transactionId?: string;
    purpose?: string; // e.g., 'zakat', 'sadaqah', 'construction'
    invoiceNumber?: string; // Auto-generated for donations

    // Fee specific (can be expanded later)
    studentName?: string;
    studentId?: string;
    feeType?: string; // 'tuition', 'admission', 'exam'

    // Other Revenue specific
    category?: 'canteen' | 'uniform' | 'books' | 'rent' | 'other';
    title?: string; // For 'other' revenue title
}

export interface FinanceTransaction {
    id?: string;
    type: 'credit' | 'debit';
    amount: number;
    date: Date;
    description: string;
    category: string; // 'fee', 'donation', 'transfer', 'petty_cash', etc.
    reference?: string; // Invoice # or Transfer Ref
    performedBy: string;
    createdAt: Date;

    // Extended metadata (flexible)
    metadata?: Record<string, any>;
}

const PETTY_CASH_COLLECTION = 'petty_cash';
export const REVENUE_COLLECTION = 'revenue';
const FINANCE_ACCOUNTS_COLLECTION = 'finance_accounts';
const ACCOUNTS_PAYABLE_COLLECTION = 'accounts_payable';
const TRANSACTIONS_SUBCOLLECTION = 'transactions';
export const PETTY_CASH_ACCOUNT_ID = 'main'; // Unified ID for wallet and ledger
const PETTY_CASH_DOC_ID = PETTY_CASH_ACCOUNT_ID;
const ACCOUNT_TRANSFERS_COLLECTION = 'account_transfers';



export const FinanceService = {
    // Accounts
    async getAccounts() {
        try {
            const q = query(collection(db, FINANCE_ACCOUNTS_COLLECTION), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const accountList = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data(),
                createdAt: d.data().createdAt?.toDate?.(),
                updatedAt: d.data().updatedAt?.toDate?.(),
            } as FinanceAccount));

            // Append Petty Cash Wallet as a virtual account
            const wallet = await this.getPettyCashWallet();
            if (wallet) {
                accountList.push({
                    id: PETTY_CASH_ACCOUNT_ID,
                    name: 'Petty Cash Book',
                    type: 'petty_cash',
                    balance: wallet.balance,
                    openingBalance: 0,
                    createdAt: new Date(0), // Placeholder
                    updatedAt: wallet.updatedAt
                });
            }

            return accountList;
        } catch (error) {
            console.error('Error fetching finance accounts:', error);
            throw error;
        }
    },

    createAccount: async (account: Omit<FinanceAccount, 'id' | 'balance' | 'createdAt' | 'updatedAt'>) => {
        try {
            const openingBalance = Number(account.openingBalance || 0);
            const now = Timestamp.now();
            const docRef = await addDoc(collection(db, FINANCE_ACCOUNTS_COLLECTION), {
                ...account,
                openingBalance,
                balance: openingBalance,
                createdAt: now,
                updatedAt: now,
            });
            return docRef.id;
        } catch (error) {
            console.error('Error creating finance account:', error);
            throw error;
        }
    },

    getPettyCashTransfers: async (): Promise<PettyCashTransfer[]> => {
        try {
            const q = query(
                collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION),
                orderBy('date', 'desc')
            );
            const snap = await getDocs(q);
            return snap.docs.map(d => {
                const data: any = d.data();
                return {
                    id: d.id,
                    ...data,
                    createdAt: data.createdAt?.toDate?.() ?? new Date(0),
                } as any;
            });
        } catch (error) {
            console.error('Error fetching petty cash transfers:', error);
            throw error;
        }
    },

    getAccountTransfers: async (): Promise<AccountTransfer[]> => {
        try {
            const q = query(collection(db, ACCOUNT_TRANSFERS_COLLECTION), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            return snap.docs.map(d => {
                const data: any = d.data();
                return {
                    id: d.id,
                    ...data,
                    createdAt: data.createdAt?.toDate?.() ?? new Date(0),
                } as AccountTransfer;
            });
        } catch (error) {
            console.error('Error fetching account transfers:', error);
            throw error;
        }
    },

    // Petty Cash Wallet (balance container)
    getPettyCashWallet: async (): Promise<PettyCashWallet> => {
        try {
            const ref = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);
            const snap = await getDoc(ref);
            if (!snap.exists()) {
                // Lazily initialize
                await runTransaction(db, async (tx) => {
                    const s = await tx.get(ref);
                    if (!s.exists()) {
                        tx.set(ref, { balance: 0, totalReceived: 0, updatedAt: Timestamp.now() });
                    }
                });
                const after = await getDoc(ref);
                return {
                    id: after.id,
                    balance: (after.data()?.balance ?? 0) as number,
                    updatedAt: after.data()?.updatedAt?.toDate?.(),
                };
            }
            return {
                id: snap.id,
                balance: (snap.data()?.balance ?? 0) as number,
                updatedAt: snap.data()?.updatedAt?.toDate?.(),
            };
        } catch (error) {
            console.error('Error fetching petty cash wallet:', error);
            throw error;
        }
    },

    transferToPettyCash: async (params: { fromAccountId: string; amount: number; performedBy: string; note?: string }) => {
        return await FinanceService.transferToPettyCashMulti({
            lines: [{ fromAccountId: params.fromAccountId, amount: params.amount }],
            performedBy: params.performedBy,
            note: params.note,
        });
    },

    transferToPettyCashMulti: async (params: { lines: PettyCashTransferLine[]; performedBy: string; note?: string }) => {
        const { lines, performedBy, note } = params;
        if (!performedBy) throw new Error('performedBy is required');
        if (!Array.isArray(lines) || lines.length === 0) throw new Error('At least one transfer line is required');

        const cleaned = lines
            .map(l => ({ fromAccountId: String(l.fromAccountId || ''), amount: Number(l.amount || 0) }))
            .filter(l => l.fromAccountId && Number.isFinite(l.amount) && l.amount > 0);

        if (cleaned.length === 0) throw new Error('At least one valid transfer line is required');

        const walletRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);
        const totalAmount = cleaned.reduce((s, l) => s + l.amount, 0);

        await runTransaction(db, async (tx) => {
            const walletSnap = await tx.get(walletRef);
            const walletBalance = walletSnap.exists() ? Number(walletSnap.data()?.balance ?? 0) : 0;
            const currentTotalReceived = walletSnap.exists() ? Number(walletSnap.data()?.totalReceived ?? 0) : 0;
            const now = Timestamp.now();

            // Validate all accounts first
            for (const line of cleaned) {
                const accountRef = doc(db, FINANCE_ACCOUNTS_COLLECTION, line.fromAccountId);
                const accountSnap = await tx.get(accountRef);
                if (!accountSnap.exists()) throw new Error('Selected account not found');
                const accountBalance = Number(accountSnap.data()?.balance ?? 0);
                if (line.amount > accountBalance) {
                    throw new Error('Insufficient account balance for transfer. Please split across multiple accounts.');
                }
            }

            // Apply debits and record transactions
            for (const line of cleaned) {
                const accountRef = doc(db, FINANCE_ACCOUNTS_COLLECTION, line.fromAccountId);
                const accountSnap = await tx.get(accountRef);
                const accountBalance = Number(accountSnap.data()?.balance ?? 0);

                // Update Balance
                tx.update(accountRef, { balance: accountBalance - line.amount, updatedAt: now });

                // Record Debit Transaction
                const transactionRef = doc(collection(db, FINANCE_ACCOUNTS_COLLECTION, line.fromAccountId, TRANSACTIONS_SUBCOLLECTION));
                const transactionData: FinanceTransaction = {
                    type: 'debit',
                    amount: line.amount,
                    date: now.toDate(),
                    description: `Transfer to Petty Cash${note ? ': ' + note : ''}`,
                    category: 'transfer_out',
                    performedBy: performedBy,
                    createdAt: now.toDate(),
                    metadata: {
                        relatedLine: line,
                        note
                    }
                };

                tx.set(transactionRef, {
                    ...transactionData,
                    date: now,
                    createdAt: now
                });
            }

            // --- Record Credit Transaction for Petty Cash Wallet ---
            const walletTransactionRef = doc(collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION));
            const walletTxData: FinanceTransaction = {
                type: 'credit',
                amount: totalAmount,
                date: now.toDate(),
                description: `Received from ${cleaned.length > 1 ? 'Multiple Accounts' : 'Account'}`,
                category: 'transfer_in',
                performedBy: performedBy,
                createdAt: now.toDate(),
                metadata: {
                    sourceLines: cleaned,
                    note
                }
            };
            tx.set(walletTransactionRef, {
                ...walletTxData,
                date: now,
                createdAt: now
            });

            // Credit petty cash wallet
            if (walletSnap.exists()) tx.update(walletRef, {
                balance: walletBalance + totalAmount,
                totalReceived: currentTotalReceived + totalAmount,
                updatedAt: now
            });
            else tx.set(walletRef, {
                balance: walletBalance + totalAmount,
                totalReceived: currentTotalReceived + totalAmount,
                updatedAt: now
            });
        });
    },

    transferBetweenAccounts: async (params: { fromAccountId: string; toAccountId: string; amount: number; performedBy: string; note?: string }) => {
        const { fromAccountId, toAccountId, amount, performedBy, note } = params;
        if (!fromAccountId) throw new Error('fromAccountId is required');
        if (!toAccountId) throw new Error('toAccountId is required');
        if (fromAccountId === toAccountId) throw new Error('fromAccountId and toAccountId must be different');
        if (!performedBy) throw new Error('performedBy is required');
        if (!Number.isFinite(amount) || amount <= 0) throw new Error('amount must be > 0');

        const fromRef = doc(db, FINANCE_ACCOUNTS_COLLECTION, fromAccountId);
        const toRef = doc(db, FINANCE_ACCOUNTS_COLLECTION, toAccountId);

        await runTransaction(db, async (tx) => {
            const [fromSnap, toSnap] = await Promise.all([tx.get(fromRef), tx.get(toRef)]);
            if (!fromSnap.exists()) throw new Error('From account not found');
            if (!toSnap.exists()) throw new Error('To account not found');

            const fromBal = Number(fromSnap.data()?.balance ?? 0);
            const toBal = Number(toSnap.data()?.balance ?? 0);
            if (amount > fromBal) throw new Error('Insufficient account balance for transfer');

            const now = Timestamp.now();
            tx.update(fromRef, { balance: fromBal - amount, updatedAt: now });
            tx.update(toRef, { balance: toBal + amount, updatedAt: now });

            // Record Debit (From Account)
            const fromTxRef = doc(collection(db, FINANCE_ACCOUNTS_COLLECTION, fromAccountId, TRANSACTIONS_SUBCOLLECTION));
            const fromTxData: FinanceTransaction = {
                type: 'debit',
                amount: amount,
                date: now.toDate(),
                description: `Transfer to ${toSnap.data()?.name || 'Account'}`,
                category: 'transfer_out',
                performedBy: performedBy,
                createdAt: now.toDate(),
                metadata: { toAccountId, note }
            };
            tx.set(fromTxRef, { ...fromTxData, date: now, createdAt: now });

            // Record Credit (To Account)
            const toTxRef = doc(collection(db, FINANCE_ACCOUNTS_COLLECTION, toAccountId, TRANSACTIONS_SUBCOLLECTION));
            const toTxData: FinanceTransaction = {
                type: 'credit',
                amount: amount,
                date: now.toDate(),
                description: `Transfer from ${fromSnap.data()?.name || 'Account'}`,
                category: 'transfer_in',
                performedBy: performedBy,
                createdAt: now.toDate(),
                metadata: { fromAccountId, note }
            };
            tx.set(toTxRef, { ...toTxData, date: now, createdAt: now });

            tx.set(doc(collection(db, ACCOUNT_TRANSFERS_COLLECTION)), {
                fromAccountId,
                toAccountId,
                amount,
                performedBy,
                note: note || '',
                createdAt: now,
            });
        });
    },

    // Petty Cash Methods
    addPettyCashExpense: async (expense: Omit<PettyCashExpense, 'id' | 'createdAt' | 'voucherNumber'>) => {
        try {
            const walletRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);
            const expenseRef = doc(collection(db, PETTY_CASH_COLLECTION));

            await runTransaction(db, async (tx) => {
                const walletSnap = await tx.get(walletRef);
                const walletData = walletSnap.data();
                const walletBalance = walletSnap.exists() ? Number(walletData?.balance ?? 0) : 0;

                // Voucher Number Generation
                const lastVoucherNum = Number(walletData?.lastVoucherNum || 1511);
                const nextVoucherNum = lastVoucherNum + 1;
                const voucherNumber = `JV-${nextVoucherNum}`;

                const amountToPayNow = expense.paymentType === 'part' ? (expense.paidAmount || expense.amount) : expense.amount;

                if (!Number.isFinite(amountToPayNow) || amountToPayNow <= 0) throw new Error('Expense amount must be > 0');
                if (amountToPayNow > walletBalance) throw new Error('Insufficient petty cash balance');

                const now = Timestamp.now();
                // Update balance and voucher counter
                tx.update(walletRef, {
                    balance: walletBalance - amountToPayNow,
                    lastVoucherNum: nextVoucherNum,
                    updatedAt: now
                });

                // Create the expense record
                tx.set(expenseRef, {
                    ...expense,
                    voucherNumber,
                    createdAt: now,
                    date: Timestamp.fromDate(expense.date),
                });

                // If part payment, create Account Payable entry
                if (expense.paymentType === 'part') {
                    const payableRef = doc(collection(db, ACCOUNTS_PAYABLE_COLLECTION));
                    const totalAmount = expense.totalAmount || expense.amount;
                    const balance = totalAmount - amountToPayNow;

                    const initialHistoryEntry = {
                        date: expense.date,
                        amount: amountToPayNow,
                        performedBy: expense.submittedBy,
                        remainingBalance: balance
                    };

                    const payableData: AccountPayable = {
                        voucherNumber,
                        totalAmount,
                        paidAmount: amountToPayNow,
                        balance,
                        status: balance > 0 ? 'partial' : 'paid',
                        description: expense.description,
                        category: expense.category,
                        date: expense.date,
                        history: [initialHistoryEntry],
                        updatedAt: now.toDate()
                    };
                    tx.set(payableRef, payableData);
                }

                // --- Record Debit Transaction for Petty Cash Ledger ---
                const walletTransactionRef = doc(collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION));

                // Build payment history for metadata
                const totalAmount = expense.totalAmount || expense.amount;
                const remainingBalance = (expense.totalAmount || expense.amount) - amountToPayNow;
                const paymentHistory = expense.paymentType === 'part' ? [{
                    date: expense.date,
                    amount: amountToPayNow,
                    performedBy: expense.submittedBy,
                    remainingBalance: remainingBalance
                }] : undefined;

                const walletTxData: FinanceTransaction = {
                    type: 'debit',
                    amount: amountToPayNow,
                    date: expense.date,
                    description: expense.paymentType === 'part' ? `PART PAY: ${expense.description}` : expense.description,
                    category: 'petty_cash_expense',
                    performedBy: expense.submittedBy,
                    createdAt: now.toDate(),
                    reference: voucherNumber,
                    metadata: {
                        notes: expense.notes,
                        category: expense.category,
                        expenseId: expenseRef.id,
                        voucherNumber,
                        paymentType: expense.paymentType,
                        totalAmount: totalAmount,
                        paidAmount: amountToPayNow,
                        balanceAmount: remainingBalance,
                        remainingBalance: remainingBalance,
                        ...(paymentHistory && { paymentHistory })
                    }
                };
                tx.set(walletTransactionRef, {
                    ...walletTxData,
                    date: Timestamp.fromDate(expense.date),
                    createdAt: now
                });
            });

            return expenseRef.id;
        } catch (error) {
            console.error('Error adding petty cash expense:', error);
            throw error;
        }
    },

    payAccountPayable: async (payableId: string, amount: number, performedBy: string) => {
        const payableRef = doc(db, ACCOUNTS_PAYABLE_COLLECTION, payableId);
        const walletRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);

        await runTransaction(db, async (tx) => {
            const payableSnap = await tx.get(payableRef);
            if (!payableSnap.exists()) throw new Error('Account Payable not found');

            const walletSnap = await tx.get(walletRef);
            const walletBalance = Number(walletSnap.data()?.balance ?? 0);

            if (amount > walletBalance) throw new Error('Insufficient petty cash balance');

            const payableData = payableSnap.data() as AccountPayable;
            const newPaidAmount = (payableData.paidAmount || 0) + amount;
            const newBalance = (payableData.totalAmount || 0) - newPaidAmount;
            const now = Timestamp.now();

            // Build updated payment history
            const newHistoryEntry = {
                date: now.toDate(),
                amount,
                performedBy,
                remainingBalance: newBalance
            };
            const updatedHistory = [...(payableData.history || []), newHistoryEntry];

            // Update Payable
            tx.update(payableRef, {
                paidAmount: newPaidAmount,
                balance: newBalance,
                status: newBalance <= 0 ? 'paid' : 'partial',
                history: updatedHistory,
                updatedAt: now
            });

            // Update Wallet
            tx.update(walletRef, {
                balance: walletBalance - amount,
                updatedAt: now
            });

            // Record Ledger Transaction (Reuse Voucher Number) with full payment history
            const walletTransactionRef = doc(collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION));
            const walletTxData: FinanceTransaction = {
                type: 'debit',
                amount: amount,
                date: now.toDate(),
                description: `BALANCE PAY: ${payableData.description}`,
                category: 'petty_cash_expense',
                performedBy,
                createdAt: now.toDate(),
                reference: payableData.voucherNumber,
                metadata: {
                    payableId: payableId,
                    voucherNumber: payableData.voucherNumber,
                    paymentType: 'balance',
                    totalAmount: payableData.totalAmount,
                    paidNow: amount,
                    remainingBalance: newBalance,
                    balanceAmount: newBalance, // For backward compatibility
                    paymentHistory: updatedHistory // Include full payment history
                }
            };
            tx.set(walletTransactionRef, {
                ...walletTxData,
                date: now,
                createdAt: now
            });
        });
    },

    getUnpaidPayables: async () => {
        const q = query(
            collection(db, ACCOUNTS_PAYABLE_COLLECTION),
            where('status', 'in', ['unpaid', 'partial']),
            orderBy('date', 'desc')
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as AccountPayable));
    },

    getPettyCashExpenses: async (filters?: { status?: string, startDate?: Date, endDate?: Date }) => {
        try {
            // Fetch ordered by date first
            const q = query(
                collection(db, PETTY_CASH_COLLECTION),
                orderBy('date', 'desc')
            );

            const querySnapshot = await getDocs(q);
            let expenses = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().date.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
            } as PettyCashExpense));

            // Client-side filtering to avoid composite index requirements
            if (filters?.status) {
                expenses = expenses.filter(e => e.status === filters.status);
            }

            return expenses;
        } catch (error) {
            console.error('Error fetching petty cash expenses:', error);
            throw error;
        }
    },

    submitPettyCashExpenses: async (expenseIds: string[], batchId?: string) => {
        try {
            const status: PettyCashExpense['status'] = 'submitted';
            const promises = expenseIds.map(id =>
                updateDoc(doc(db, PETTY_CASH_COLLECTION, id), {
                    status,
                    ...(batchId && { batchId })
                })
            );
            await Promise.all(promises);
        } catch (error) {
            console.error('Error submitting petty cash expenses:', error);
            throw error;
        }
    },

    // Mutation for updating a single expense status (e.g. approve/reject)
    updatePettyCashStatus: async (id: string, status: PettyCashExpense['status']) => {
        try {
            const expenseRef = doc(db, PETTY_CASH_COLLECTION, id);
            const walletRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);

            await runTransaction(db, async (tx) => {
                const [expenseSnap, walletSnap] = await Promise.all([tx.get(expenseRef), tx.get(walletRef)]);
                if (!expenseSnap.exists()) throw new Error('Expense not found');

                const prevStatus = expenseSnap.data()?.status as PettyCashExpense['status'];
                const amount = Number(expenseSnap.data()?.amount ?? 0);
                const walletBalance = walletSnap.exists() ? Number(walletSnap.data()?.balance ?? 0) : 0;
                const now = Timestamp.now();

                // Refund logic: if moving into rejected from non-rejected, refund amount
                if (status === 'rejected' && prevStatus !== 'rejected') {
                    tx.update(walletRef, { balance: walletBalance + amount, updatedAt: now });
                    // Also need to record a counter-transaction? 
                    // Actually, simple refund is better. Let's add a Credit transaction to the ledger for the refund.
                    const refundTxRef = doc(collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION));
                    tx.set(refundTxRef, {
                        type: 'credit',
                        amount: amount,
                        date: now,
                        description: `Refund: Rejected Expense (${expenseSnap.data()?.description})`,
                        category: 'refund',
                        performedBy: 'system',
                        createdAt: now,
                        metadata: { expenseId: id }
                    });
                }
                // If moving out of rejected into a valid spending status, re-deduct (if possible)
                if (prevStatus === 'rejected' && status !== 'rejected') {
                    if (amount > walletBalance) throw new Error('Insufficient petty cash balance to restore this expense');
                    tx.update(walletRef, { balance: walletBalance - amount, updatedAt: now });

                    const deductTxRef = doc(collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION));
                    tx.set(deductTxRef, {
                        type: 'debit',
                        amount: amount,
                        date: now,
                        description: `Re-deduct: Restored Expense (${expenseSnap.data()?.description})`,
                        category: 'petty_cash_expense',
                        performedBy: 'system',
                        createdAt: now,
                        metadata: { expenseId: id }
                    });
                }

                tx.update(expenseRef, { status });
            });
        } catch (error) {
            console.error('Error updating petty cash status:', error);
            throw error;
        }
    },

    // Revenue Methods (Consolidated)
    // Revenue Methods (Refactored to use Sub-collections)
    addRevenue: async (revenue: Omit<Revenue, 'id'>) => {
        try {
            let invoiceNumber: string | undefined = undefined;
            if (revenue.type === 'donation') {
                invoiceNumber = `DON-${Date.now().toString().slice(-6)}`;
            } else if (revenue.type === 'fee') {
                invoiceNumber = `FEE-${Date.now().toString().slice(-6)}`;
            } else {
                invoiceNumber = `REV-${Date.now().toString().slice(-6)}`;
            }

            if (!revenue.accountId) {
                throw new Error('Account is required for revenue entry');
            }

            const accountRef = doc(db, FINANCE_ACCOUNTS_COLLECTION, revenue.accountId);
            // New sub-collection structure
            const transactionRef = doc(collection(db, FINANCE_ACCOUNTS_COLLECTION, revenue.accountId, TRANSACTIONS_SUBCOLLECTION));

            await runTransaction(db, async (tx) => {
                const accountSnap = await tx.get(accountRef);
                if (!accountSnap.exists()) throw new Error('Selected account not found');
                const accountBalance = Number(accountSnap.data()?.balance ?? 0);
                const amount = Number(revenue.amount || 0);
                if (!Number.isFinite(amount) || amount <= 0) throw new Error('Revenue amount must be > 0');

                const now = Timestamp.now();

                // 1. Update Account Balance and Total Received
                const currentTotalReceived = Number(accountSnap.data()?.totalReceived ?? 0);
                tx.update(accountRef, {
                    balance: accountBalance + amount,
                    totalReceived: currentTotalReceived + amount,
                    updatedAt: now
                });

                // 2. Add Transaction Record in Sub-collection
                const transactionData: FinanceTransaction = {
                    type: 'credit', // Revenue is a credit to the account
                    amount: amount,
                    date: revenue.date, // User selected date
                    description: revenue.type === 'donation' ? `Donation: ${revenue.donorName}` : (revenue.title || revenue.description || 'Revenue'),
                    category: revenue.type,
                    reference: invoiceNumber,
                    performedBy: revenue.receivedBy,
                    createdAt: now.toDate(),
                    metadata: {
                        ...revenue, // Store full revenue object as metadata for reporting
                        invoiceNumber
                    }
                };

                // Convert Date objects to Timestamps for Firestore
                const firestoreData = {
                    ...transactionData,
                    date: Timestamp.fromDate(transactionData.date),
                    createdAt: Timestamp.fromDate(transactionData.createdAt)
                };

                tx.set(transactionRef, firestoreData);
            });

            return { id: transactionRef.id, invoiceNumber };
        } catch (error) {
            console.error('Error adding revenue:', error);
            throw error;
        }
    },

    ensureVoucherNumber: async (transactionId: string) => {
        const walletRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID);
        const txRef = doc(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION, transactionId);
        const txSnap = await getDoc(txRef);

        if (!txSnap.exists()) throw new Error('Transaction not found');

        const txData = txSnap.data();

        if (txData.reference || txData.metadata?.voucherNumber) {
            return txData.reference || txData.metadata?.voucherNumber;
        }

        return await runTransaction(db, async (tx) => {
            const walletSnap = await tx.get(walletRef);
            const lastVoucherNum = Number(walletSnap.data()?.lastVoucherNum || 1511);
            const nextVoucherNum = lastVoucherNum + 1;
            const voucherNumber = `JV-${nextVoucherNum}`;

            tx.update(txRef, {
                reference: voucherNumber,
                'metadata.voucherNumber': voucherNumber
            });

            tx.update(walletRef, {
                lastVoucherNum: nextVoucherNum
            });

            return voucherNumber;
        });
    },

    // New Method: Get Transactions for specific account
    getAccountTransactions: async (accountId: string) => {
        try {
            let q;
            if (accountId === PETTY_CASH_ACCOUNT_ID) {
                q = query(
                    collection(db, PETTY_CASH_COLLECTION, PETTY_CASH_DOC_ID, TRANSACTIONS_SUBCOLLECTION),
                    orderBy('date', 'desc')
                );
            } else {
                q = query(
                    collection(db, FINANCE_ACCOUNTS_COLLECTION, accountId, TRANSACTIONS_SUBCOLLECTION),
                    orderBy('date', 'desc')
                );
            }
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({
                id: d.id,
                ...d.data(),
                date: d.data().date?.toDate?.(),
                createdAt: d.data().createdAt?.toDate?.(),
            } as FinanceTransaction));
        } catch (error) {
            console.error('Error fetching transactions:', error);
            throw error;
        }
    },

    // New Method: Get All Transactions (using Collection Group Query)
    getAllTransactions: async () => {
        try {
            // Note: Requires Firestore index for collectionId 'transactions' on field 'date' DESC
            const q = query(
                collectionGroup(db, TRANSACTIONS_SUBCOLLECTION),
                orderBy('date', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({
                id: d.id,
                ...d.data(),
                // We might want to inject the parent account ID here if useful
                // accountId: d.ref.parent.parent?.id, 
                date: d.data().date?.toDate(),
                createdAt: d.data().createdAt?.toDate()
            } as FinanceTransaction));
        } catch (error) {
            console.error('Error fetching all transactions:', error);
            // Fallback? Or just throw.
            throw error;
        }
    },

    getRevenue: async (type?: Revenue['type']) => {
        try {
            // Fetch ordered by date first
            const q = query(collection(db, REVENUE_COLLECTION), orderBy('date', 'desc'));

            const querySnapshot = await getDocs(q);
            let revenues = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().date.toDate(),
            } as Revenue));

            // Client-side filtering to avoid composite index requirements
            if (type) {
                revenues = revenues.filter(r => r.type === type);
            }

            return revenues;
        } catch (error) {
            console.error('Error fetching revenue:', error);
            throw error;
        }
    },

    resolveUserName: async (uid: string): Promise<string> => {
        if (!uid || uid === 'unknown' || uid === 'System') return 'N/A';
        try {
            // Check users collection first
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) return userDoc.data().displayName || 'Unknown User';

            // Check staff
            const staffDoc = await getDoc(doc(db, 'staff', uid));
            if (staffDoc.exists()) return `${staffDoc.data().firstName} ${staffDoc.data().lastName}`;

            // Check teachers
            const teacherDoc = await getDoc(doc(db, 'teachers', uid));
            if (teacherDoc.exists()) return `${teacherDoc.data().firstName} ${teacherDoc.data().lastName}`;

            // Check students
            const studentDoc = await getDoc(doc(db, 'students', uid));
            if (studentDoc.exists()) return `${studentDoc.data().firstName} ${studentDoc.data().lastName}`;

            return 'N/A';
        } catch (error) {
            console.error(`Error resolving user name for ${uid}:`, error);
            return 'N/A';
        }
    },
};
